/**
 * Loan（/app/borrower/:id）。单笔贷款工作台，菜单没有单独项。
 * 打开页面打 STATSIG.COMMON.VIEW，value=loan。
 *
 * 当前页面在用的壳子功能：
 * - 顶栏：Stage/Status、Export、借款人档案、Rate
 * - 左侧 Stepper：OVERVIEW / APPLICATION / PROCESSING
 *
 * 进页打 PAGE_VIEW。左侧切菜单一律 TAB，value 用菜单名的 snake_case（credit_pull / rate_recommendation）。
 * Documents tab 内部：建议/套用/保存模板、发 request、LO 上传、Verify。
 * 其余 tab 内部操作后续单独拆。Stage/Status 先不算。
 * 顶栏 Rate 点开弹窗是 RATE_SELECTION_BUTTON，点 Done 保存是 RATE_SELECTION。
 * 1003 顶栏 Add co-borrower 添加成功记 ADD_CO_BORROWER_BUTTON（value=loan_id）。
 * 删除 co-borrower 记 DELETE_CO_BORROWER_BUTTON（value=delete）。
 * 顶栏切换 borrower 记 BORROWER_SWITCH（value 为切到的 party_id）。
 * Income calculation 记 INCOME_CALCULATION。
 * Import from Credit Report 记 IMPORT_FROM_CREDIT_REPORT。
 * 1003 个人资料 PATCH 落库记 UPDATE_1003。
 * Settings Document Templates 库本身不跨到这里。
 */
export enum LoanEvent {
    /**
     * 切左侧菜单。
     * value 与 metadata.tab 相同，如 credit_pull、rate_recommendation
     */
    TAB = 'LOAN_TAB',
    /**
     * 1003 顶栏 Add co-borrower 添加成功。
     * value 与 metadata.loan_id 相同
     */
    ADD_CO_BORROWER_BUTTON = 'LOAN_ADD_CO_BORROWER_BUTTON',
    /**
     * 1003 顶栏确认删除 co-borrower。
     * value 与 metadata.type 相同：delete
     * metadata.borrower_count 为删除后人数；metadata.party_id 为被删的 party
     */
    DELETE_CO_BORROWER_BUTTON = 'LOAN_DELETE_CO_BORROWER_BUTTON',
    /**
     * 1003 顶栏切换 borrower。
     * value 与 metadata.party_id 相同，为切到的 borrower party id
     * metadata.role=borrower|coborrower
     */
    BORROWER_SWITCH = 'LOAN_BORROWER_SWITCH',
    /**
     * 1003 个人资料落库。PATCH /los/v2/loans/:loanId/participants/:partyId/profile
     * value 与 metadata.party_id 相同，为被更新的 borrower party id
     */
    UPDATE_1003 = 'LOAN_1003_UPDATE',
    /**
     * 导出：点 Export 打开下拉，或点具体格式。
     * value 与 metadata.type 相同：click_open_dropdown | PDF_1003 | FNM_3.4
     */
    EXPORT = 'LOAN_EXPORT',
    /**
     * 顶栏 Rate：点 Rate / Select 打开选利率弹窗。
     * value 与 metadata.type 相同：click_open_popup
     */
    RATE_SELECTION_BUTTON = 'LOAN_RATE_SELECTION_BUTTON',
    /**
     * 顶栏 Rate 弹窗点 Done 保存所选利率。
     * value 与 metadata.type 相同：done
     */
    RATE_SELECTION = 'LOAN_RATE_SELECTION',
    /**
     * 1003 Income calculation：打开弹窗、算出结果、写回 1003。
     * value 与 metadata.type 相同：click_open_popup | calculation_result | update_to_1003
     * metadata.kind=wage|self_employed
     * metadata.section=1b|1c
     */
    INCOME_CALCULATION = 'LOAN_INCOME_CALCULATION',
    /**
     * 1003 Import from Credit Report / Credit tab Import to 1003。
     * value 与 metadata.type 相同：click_open_popup | insert
     * metadata.section=section2c|section3
     */
    IMPORT_FROM_CREDIT_REPORT = 'LOAN_IMPORT_FROM_CREDIT_REPORT',
    /** 顶栏点借款人，进入档案 */
    BORROWER_OPEN = 'LOAN_BORROWER_OPEN',
    /**
     * Documents：建议模板、套用模板、或把当前 checklist 存成模板。
     * 属性 type=suggested|applied|saved
     * 属性 source=add_document_request|template_picker|template_library|document_request
     */
    DOCUMENT_TEMPLATE = 'LOAN_DOCUMENT_TEMPLATE',
    /**
     * Documents：Send request 落库。
     * 属性 type=checklist|single
     */
    DOCUMENT_REQUEST = 'LOAN_DOCUMENT_REQUEST',
    /** Documents：LO 上传文件 */
    DOCUMENT_UPLOAD = 'LOAN_DOCUMENT_UPLOAD',
    /**
     * Documents：Verify 完成。
     * 属性 type=verify|manual
     * value 为耗时毫秒
     */
    DOCUMENT_PROCESS = 'LOAN_DOCUMENT_PROCESS',
}

/** Stepper tabKey → LOAN_TAB value（菜单显示名） */
export const LOAN_TAB_VALUE: Record<string, string> = {
    loanSummary: 'overview',
    '1003View': '1003',
    borrowerView: 'borrower_application',
    hardMoneyLoanBorrower: 'borrower',
    hardMoneyLoanProperty: 'property_and_loan',
    hardMoneyLoanExperience: 'experience',
    hardMoneyLoanDeclarations: 'declarations',
    creditReport: 'credit_pull',
    rates: 'rate_recommendation',
    prequalLetter: 'pre_qual_letter',
    escrowInfo: 'escrow',
    documentChecklist: 'documents',
    UWList: 'underwriting_checklist',
    appraisalInfo: 'appraisal',
    preapprovalLetter: 'pre_approval_letter',
    tasks: 'tasks',
    comment: 'communications',
    mismo: 'fnm_3_4',
    closing: 'closing',
}

export function loanTabValue(tabKey: string): string {
    return LOAN_TAB_VALUE[tabKey] || tabKey
}
